import { format, parseISO, startOfWeek } from 'date-fns';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { formatExportDateAndTime } from 'utils/formatExportDateAndTime';
import { isLineLiveOrEnded } from 'utils/isLineLiveOrEnded';
import { ProgrammaticAndDirectSalesBookingStatus } from 'components/common/types/Deal.types';
import { AllocationDay, AllocationReportData, AllocationWeek, Frame, SelectedLine } from './AllocationReport.types';
import { AggregatedFrame, FramesReportResponse, TransformedFramesReport } from './FramesReport/FramesReport.types';
import { metricTypeToName } from '../DrilldownReports/consts';

export const generateFileName = ({
  activeReportType,
  selectedMetricType,
  selectedLine,
  selectedLineCount = 0,
}: {
  activeReportType: string;
  selectedMetricType: MetricType;
  selectedLine: SelectedLine;
  selectedLineCount?: number;
}): string => {
  const reportType = activeReportType.replaceAll(' ', '_');
  const dealLineName =
    selectedLineCount > 1 ? `${selectedLineCount}_Deal_Lines` : selectedLine.lineName.slice(0, 35).replaceAll(' ', '_');

  return `${reportType}_Report_${metricTypeToName[selectedMetricType]}_${dealLineName}_${formatExportDateAndTime(new Date())}`;
};

export const generateFramesReportFileName = ({ campaignName }: { campaignName: string }): string => {
  const maxNameLength = 35;

  const trimmedCampaignName = campaignName.slice(0, maxNameLength).replaceAll(' ', '_');

  return `Frame_List_${trimmedCampaignName}_${formatExportDateAndTime(new Date())}`;
};

export const formatToPercentage = (value: number, decimalPlaces?: number): string => {
  return `${(value * 100).toFixed(decimalPlaces ?? 0)}%`;
};

export const transformFrameListReport = (framesReport: FramesReportResponse): TransformedFramesReport => {
  const mergedFrameImpressionsAndMetaData = Object.fromEntries(
    Object.entries(framesReport.report).map(([idKey, idValues]) => [
      idKey,
      idValues.map((item) => ({
        ...item,
        dealLineId: idKey,
        ...(framesReport.metadata[item.frameId] || {}),
      })),
    ]),
  );

  return Object.values(mergedFrameImpressionsAndMetaData).flat();
};

export const transformSelectedLinesToReport = (
  selectedDealLines: string[],
  lineReports: TransformedFramesReport,
): {
  reportLines: AggregatedFrame[];
  totalImpressions: number;
  totalPrice: number;
  totalIndexedPrice: number;
} => {
  const reportLines = selectedDealLines
    .flatMap((dealLineId) => lineReports.filter((line) => line.dealLineId === dealLineId))
    .reduce((accMap, line) => {
      const existing = accMap.get(line.frameId);

      if (existing) {
        existing.impression += line.impression;
        existing.price = (existing.price ?? 0) + (line.price ?? 0);
        existing.indexedPrice = (existing.indexedPrice ?? 0) + (line.indexedPrice ?? 0);
        if (!existing.dealLineIds.includes(line.dealLineId)) {
          existing.dealLineIds.push(line.dealLineId);
        }
      } else {
        accMap.set(line.frameId, {
          ...line,
          impression: line.impression,
          price: line.price ?? 0,
          indexedPrice: line.indexedPrice ?? 0,
          dealLineIds: [line.dealLineId],
        });
      }

      return accMap;
    }, new Map<string, AggregatedFrame>());

  const transformedReportLines = Array.from(reportLines.values());

  return {
    totalImpressions: transformedReportLines.reduce((acc, curr) => acc + curr.impression, 0),
    totalPrice: transformedReportLines.reduce((acc, curr) => acc + (curr.price ?? 0), 0),
    totalIndexedPrice: transformedReportLines.reduce((acc, curr) => acc + (curr.indexedPrice ?? 0), 0),
    reportLines: transformedReportLines,
  };
};

const cloneDay = (day: AllocationDay): AllocationDay => ({
  ...day,
  hours: day.hours ? [...day.hours] : undefined,
});

const mergeAllocationDays = (
  baseDays: AllocationDay[],
  incomingDays: AllocationDay[],
  shouldSum = true,
): AllocationDay[] => {
  const merge = shouldSum ? (a: number, b: number) => a + b : Math.max;

  const dayMap = baseDays.reduce((map, day) => {
    map.set(day.date, cloneDay(day));
    return map;
  }, new Map<string, AllocationDay>());

  incomingDays.forEach((day) => {
    const existing = dayMap.get(day.date);

    if (!existing) {
      dayMap.set(day.date, cloneDay(day));
      return;
    }

    existing.total = merge(existing.total, day.total);

    if (existing.hours && day.hours) {
      existing.hours = existing.hours.map((val, idx) => merge(val, day.hours![idx] ?? 0));
    } else if (!existing.hours && day.hours) {
      existing.hours = [...day.hours];
    }
  });

  return Array.from(dayMap.values()).sort((dayA, dayB) => dayA.date.localeCompare(dayB.date));
};

type AllocationWeekInput = Omit<AllocationWeek, 'allocationWeekStartDate'> & {
  allocationWeekStartDate?: string;
};

const getWeekKey = (week: AllocationWeekInput): string => {
  if (week.days.length === 0) return String(week.allocationWeek);
  const monday = startOfWeek(parseISO(week.days[0].date), { weekStartsOn: 1 });
  return format(monday, 'yyyy-MM-dd');
};

const withAllocationWeekStartDate = (week: AllocationWeekInput): AllocationWeek => ({
  ...week,
  allocationWeekStartDate: getWeekKey(week),
});

const cloneWeek = (week: AllocationWeekInput): AllocationWeek =>
  withAllocationWeekStartDate({
    ...week,
    days: week.days.map((day) => ({ ...day })),
  });

export const withAllocationWeekStartDates = (report: AllocationReportData): AllocationReportData => ({
  ...report,
  frames: report.frames.map((frame) => ({
    ...frame,
    allocations: frame.allocations.map(withAllocationWeekStartDate),
  })),
  totalAllocations: (report.totalAllocations ?? []).map(withAllocationWeekStartDate),
});

export const findAllocationWeek = (
  allocations: AllocationWeek[] | undefined,
  weekStartDate: string,
  weekIndex: number,
): AllocationWeek | undefined => {
  if (!allocations?.length) return undefined;

  if (allocations.some((week) => week.allocationWeekStartDate)) {
    return allocations.find((week) => week.allocationWeekStartDate === weekStartDate);
  }

  return allocations[weekIndex];
};

export const findAllocationDay = (week: AllocationWeek | undefined, dayDate: Date): AllocationDay | undefined => {
  if (!week?.days?.length) return undefined;

  const dateKey = format(dayDate, 'yyyy-MM-dd');

  return week.days.find((day) => day.date.slice(0, 10) === dateKey);
};

const mergeAllocationWeeks = (
  baseWeeks: AllocationWeek[],
  incomingWeeks: AllocationWeek[],
  shouldSum = true,
): AllocationWeek[] => {
  const incomingWeeksWithStartDate = incomingWeeks.map(withAllocationWeekStartDate);
  const baseWeeksWithStartDate = baseWeeks.map(withAllocationWeekStartDate);

  const merge = shouldSum ? (a: number, b: number) => a + b : Math.max;

  const weekMap = baseWeeksWithStartDate.reduce((map, week) => {
    map.set(getWeekKey(week), cloneWeek(week));
    return map;
  }, new Map<string, AllocationWeek>());

  incomingWeeksWithStartDate.forEach((week) => {
    const key = getWeekKey(week);
    const existing = weekMap.get(key);

    if (!existing) {
      weekMap.set(key, cloneWeek(week));
      return;
    }

    existing.total = merge(existing.total, week.total);
    existing.days = mergeAllocationDays(existing.days, week.days, shouldSum);
  });

  return Array.from(weekMap.values())
    .sort((a, b) => getWeekKey(a).localeCompare(getWeekKey(b)))
    .map((week, index) => withAllocationWeekStartDate({ ...week, allocationWeek: index + 1 }));
};

const cloneFrame = (frame: Frame): Frame => ({
  ...frame,
  total: frame.total ?? 0,
  allocations: frame.allocations.map((week) =>
    withAllocationWeekStartDate({
      ...week,
      days: week.days.map((day) => cloneDay(day)),
    }),
  ),
});

const addFrameToMap = (frameMap: Map<string, Frame>, frame: Frame, shouldSumMetric: boolean): void => {
  const existing = frameMap.get(frame.frameId);

  if (!existing) {
    frameMap.set(frame.frameId, cloneFrame(frame));
    return;
  }

  const existingTotal = existing.total ?? 0;
  const incomingTotal = frame.total ?? 0;

  existing.total = shouldSumMetric ? existingTotal + incomingTotal : Math.max(existingTotal, incomingTotal);
  existing.allocations = mergeAllocationWeeks(existing.allocations, frame.allocations, shouldSumMetric);
};

const mergeFrames = (allFrameArrays: Frame[][], shouldSumMetric: boolean): Frame[] => {
  const frameMap = new Map<string, Frame>();

  allFrameArrays.forEach((frames) => {
    frames.forEach((frame) => addFrameToMap(frameMap, frame, shouldSumMetric));
  });

  return Array.from(frameMap.values());
};

const buildZeroDay = (refDay: AllocationDay): AllocationDay => ({
  date: refDay.date,
  total: 0,
  hours: refDay.hours ? refDay.hours.map(() => 0) : undefined,
});

const padFrameAllocations = (frame: Frame, referenceWeeks: AllocationWeek[]): Frame => {
  const frameWeekMap = new Map(frame.allocations.map((week) => [getWeekKey(week), week]));

  const paddedAllocations = referenceWeeks.map((refWeek) => {
    const existing = frameWeekMap.get(getWeekKey(refWeek));

    if (!existing) {
      return withAllocationWeekStartDate({
        allocationWeek: refWeek.allocationWeek,
        total: 0,
        days: refWeek.days.map(buildZeroDay),
      });
    }

    const existingDayMap = new Map(existing.days.map((day) => [day.date, day]));
    const paddedDays = refWeek.days.map((refDay) => existingDayMap.get(refDay.date) ?? buildZeroDay(refDay));

    return withAllocationWeekStartDate({ ...existing, days: paddedDays });
  });

  return { ...frame, allocations: paddedAllocations };
};

const computeTotalsFromReports = (
  reports: AllocationReportData[],
): { mergedTotal: number; mergedTotalAllocations: AllocationWeek[] } => {
  const mergedTotal = reports.reduce((sum, report) => sum + report.total, 0);

  const mergedTotalAllocations = reports.reduce<AllocationWeek[]>(
    (merged, report) =>
      report.totalAllocations?.length > 0 ? mergeAllocationWeeks(merged, report.totalAllocations) : merged,
    [],
  );

  return { mergedTotal, mergedTotalAllocations };
};

const computeTotalsFromFrames = (
  frames: Frame[],
): { mergedTotal: number; mergedTotalAllocations: AllocationWeek[] } => {
  const mergedTotal = frames.reduce((sum, frame) => sum + (frame.total ?? 0), 0);

  const mergedTotalAllocations = frames.reduce<AllocationWeek[]>(
    (merged, frame) =>
      merged.length === 0 ? frame.allocations.map(cloneWeek) : mergeAllocationWeeks(merged, frame.allocations),
    [],
  );

  return { mergedTotal, mergedTotalAllocations };
};

const nonZeroAverage = (values: number[]): number => {
  const nonZero = values.filter((value) => value > 0);
  if (nonZero.length === 0) return 0;
  return nonZero.reduce((a, b) => a + b, 0) / nonZero.length;
};

const recomputeSotFrameTotals = (frame: Frame): Frame => {
  const allocations = frame.allocations.map((week) => ({
    ...week,
    total: nonZeroAverage(week.days.map((day) => day.total)),
  }));

  const total = nonZeroAverage(allocations.flatMap((week) => week.days.map((day) => day.total)));

  return { ...frame, allocations, total };
};

export const isShareOfTimeMetric = (metricType: MetricType): boolean =>
  metricType === MetricType.SOT || metricType === MetricType.DELIVERED_SOT;

export const mergeAllocationReports = (reports: AllocationReportData[]): AllocationReportData | undefined => {
  if (reports.length === 0) return undefined;
  if (reports.length === 1) return reports[0];

  const reportsWithStartDates = reports.map(withAllocationWeekStartDates);

  const startDate = reportsWithStartDates.reduce(
    (earliest, report) => (report.startDate < earliest ? report.startDate : earliest),
    reportsWithStartDates[0].startDate,
  );
  const endDate = reportsWithStartDates.reduce(
    (latest, report) => (report.endDate > latest ? report.endDate : latest),
    reportsWithStartDates[0].endDate,
  );

  const { metricType } = reportsWithStartDates[0];
  const shouldSumMetric = metricType !== MetricType.FRAMES && !isShareOfTimeMetric(metricType);

  const mergedFrames = mergeFrames(
    reportsWithStartDates.map((report) => report.frames),
    shouldSumMetric,
  );

  const { mergedTotal, mergedTotalAllocations } = shouldSumMetric
    ? computeTotalsFromReports(reportsWithStartDates)
    : computeTotalsFromFrames(mergedFrames);

  const paddedFrames =
    mergedTotalAllocations.length > 0
      ? mergedFrames.map((frame) => padFrameAllocations(frame, mergedTotalAllocations))
      : mergedFrames;

  const framesWithTotals = isShareOfTimeMetric(metricType) ? paddedFrames.map(recomputeSotFrameTotals) : paddedFrames;

  return {
    contractVersion: reportsWithStartDates[0].contractVersion,
    frames: framesWithTotals,
    endDate,
    lineDuration: Math.max(...reportsWithStartDates.map((report) => report.lineDuration)),
    metaData: undefined,
    metricType,
    rowGroupAggFunction: reportsWithStartDates[0].rowGroupAggFunction,
    startDate,
    total: mergedTotal,
    totalAllocations: mergedTotalAllocations,
  };
};

const averageFrameDay = (
  frames: Frame[],
  weekIdx: number,
  dayIdx: number,
  templateDay: AllocationDay,
): AllocationDay => {
  const dayTotal = nonZeroAverage(frames.map((frame) => frame.allocations[weekIdx]?.days?.[dayIdx]?.total ?? 0));

  if (!templateDay.hours) return { date: templateDay.date, total: dayTotal, hours: undefined };

  const hours = templateDay.hours.map((_, hourIdx) =>
    nonZeroAverage(frames.map((frame) => frame.allocations[weekIdx]?.days?.[dayIdx]?.hours?.[hourIdx] ?? 0)),
  );

  return { date: templateDay.date, total: dayTotal, hours };
};

export const computeSOTAverage = (frames: Frame[]): { total: number; allocations: AllocationWeek[] } => {
  if (frames.length === 0) return { total: 0, allocations: [] };

  const total = nonZeroAverage(frames.map((frame) => frame.total ?? 0));

  const allocations = frames[0].allocations.map((templateWeek, weekIdx) => ({
    allocationWeek: templateWeek.allocationWeek,
    allocationWeekStartDate: templateWeek.allocationWeekStartDate,
    total: nonZeroAverage(frames.map((frame) => frame.allocations[weekIdx]?.total ?? 0)),
    days: templateWeek.days.map((templateDay, dayIdx) => averageFrameDay(frames, weekIdx, dayIdx, templateDay)),
  }));

  return { total, allocations };
};

export const isDeliveredMetricSelected = (selectedMetricType: MetricType): boolean => {
  return [MetricType.DELIVERED_IMPRESSIONS, MetricType.DELIVERED_SOT, MetricType.DELIVERED_PLAYS].includes(
    selectedMetricType,
  );
};

export const isLineIneligibleForDeliveredMetrics = (
  selectedMetricType: MetricType,
  lineStatus?: ProgrammaticAndDirectSalesBookingStatus,
): boolean => isDeliveredMetricSelected(selectedMetricType) && !!lineStatus && !isLineLiveOrEnded(lineStatus);
